import {
  type EnvironmentId,
  TRANSCRIPTIONS_ROUTE_PATH,
  TranscriptionResponse,
  VOICE_TRANSCRIPTION_MAX_BYTES,
} from "@t3tools/contracts";
import * as Schema from "effect/Schema";

import { resolvePrimaryEnvironmentHttpUrl } from "./environments/primary/target";
import { readEnvironmentConnection } from "./environments/runtime";

const decodeTranscriptionResponse = Schema.decodeUnknownSync(TranscriptionResponse);

export type VoiceTranscriptionFailure =
  | { readonly kind: "disconnected" }
  | { readonly kind: "network" }
  | { readonly kind: "timeout" }
  | { readonly kind: "tooLarge" }
  | { readonly kind: "http"; readonly status: number; readonly detail: string }
  | { readonly kind: "invalidResponse" }
  | { readonly kind: "empty" }
  | { readonly kind: "interrupted"; readonly phase: "recording" | "transcribing" }
  | { readonly kind: "tooLong" }
  | { readonly kind: "audioUnavailable" }
  | { readonly kind: "unknown"; readonly detail: string };

export class VoiceTranscriptionError extends Error {
  readonly failure: VoiceTranscriptionFailure;

  constructor(failure: VoiceTranscriptionFailure) {
    super(`Voice transcription failed: ${failure.kind}`);
    this.name = "VoiceTranscriptionError";
    this.failure = failure;
  }
}

export function isTranscriptionAbortedError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

export function toVoiceTranscriptionFailure(error: unknown): VoiceTranscriptionFailure {
  if (error instanceof VoiceTranscriptionError) return error.failure;
  if (error instanceof DOMException && error.name === "TimeoutError") return { kind: "timeout" };
  if (error instanceof TypeError) return { kind: "network" };
  return { kind: "unknown", detail: error instanceof Error ? error.message : String(error) };
}

const BASE_TIMEOUT_MS = 120_000;
const UPLOAD_TIMEOUT_MS_PER_MEGABYTE = 10_000;

export function voiceTranscriptionTimeoutMs(sizeBytes: number): number {
  return BASE_TIMEOUT_MS + Math.ceil(sizeBytes / (1024 * 1024)) * UPLOAD_TIMEOUT_MS_PER_MEGABYTE;
}

export interface VoiceTranscriptionRequest {
  readonly environmentId: EnvironmentId;
  readonly audio: Blob;
  readonly mimeType: string;
  readonly signal: AbortSignal;
}

export interface VoiceTranscriptionTransport {
  readonly fetch?: typeof fetch;
  readonly timeoutMs?: number;
}

function resolveTranscriptionUrl(environmentId: EnvironmentId): string {
  const connection = readEnvironmentConnection(environmentId);
  if (!connection) {
    throw new VoiceTranscriptionError({ kind: "disconnected" });
  }
  return connection.kind === "primary"
    ? resolvePrimaryEnvironmentHttpUrl(TRANSCRIPTIONS_ROUTE_PATH)
    : new URL(TRANSCRIPTIONS_ROUTE_PATH, connection.knownEnvironment.target.httpBaseUrl).toString();
}

export async function transcribeVoiceRecording(
  request: VoiceTranscriptionRequest,
  transport: VoiceTranscriptionTransport = {},
): Promise<string> {
  if (request.audio.size > VOICE_TRANSCRIPTION_MAX_BYTES) {
    throw new VoiceTranscriptionError({ kind: "tooLarge" });
  }
  const url = resolveTranscriptionUrl(request.environmentId);
  const fetchImpl = transport.fetch ?? fetch;
  const signal = AbortSignal.any([
    request.signal,
    AbortSignal.timeout(transport.timeoutMs ?? voiceTranscriptionTimeoutMs(request.audio.size)),
  ]);

  const response = await fetchImpl(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": request.mimeType },
    body: request.audio,
    signal,
  });
  if (!response.ok) {
    const detail = (await response.text()).trim();
    throw new VoiceTranscriptionError(
      response.status === 413
        ? { kind: "tooLarge" }
        : { kind: "http", status: response.status, detail },
    );
  }
  const body = await response.text();
  let text: string;
  try {
    text = decodeTranscriptionResponse(JSON.parse(body)).text;
  } catch {
    throw new VoiceTranscriptionError({ kind: "invalidResponse" });
  }
  if (text.trim().length === 0) {
    throw new VoiceTranscriptionError({ kind: "empty" });
  }
  return text;
}
